import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { ArrowLeft, Camera, Loader2, RefreshCw, Send, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { captureViewportScreenshot } from '@/lib/feedback/capture-viewport'
import {
  chatSendErrorMessage,
  getFeedbackChat,
  markFeedbackChatRead,
  sendFeedbackChatMessage,
} from '@/services/feedback-chat-api'
import type { FeedbackChatMessage } from '@/services/feedback-chat-api'
import type { FeedbackHistoryItem } from '@/services/feedback-api'
import { ChatAttachmentImage } from './chat-attachment-image'
import {
  buildChatEntries,
  CHAT_MAX_CHARS,
  CHAT_POLL_INTERVAL_MS,
  deriveChatTitle,
  formatMessageTime,
  latestTeamMessageId,
  teamLabel,
} from './feedback-chat-utils'
import { formatFeedbackDate, STATUS_STYLES } from './feedback-status-styles'
import { SCREENSHOT_NOT_MASKED_COPY, ScreenshotEditor } from './screenshot-editor'

type FeedbackChatProps = {
  item: FeedbackHistoryItem
  queryClient: QueryClient
  onBack: () => void
  /** Okundu işareti sunucuda yazıldığında (liste rozetini yerelde sıfırlamak için). */
  onRead: (trackingId: string) => void
  /** Ekran görüntüsü alınırken pencereyi geçici gizler/geri getirir. */
  onWindowHiddenChange: (hidden: boolean) => void
  /** Kabuğun kapatma düğmesi (Dialog.Close); başlık satırının sağına konur. */
  closeButton: ReactNode
}

const CAPTURE_FAILURE_COPY = 'Ekran görüntüsü alınamadı. Yeniden deneyebilir veya görüntüsüz devam edebilirsin.'
const LOAD_FAILURE_COPY = 'Mesajlar yüklenemedi. Bağlantını kontrol edip yeniden deneyebilirsin.'

/** Pencere gizlendikten sonra iki kare bekler; tarayıcı gizli hâli boyamış olur. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      resolve()
      return
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
}

type PendingMessage = { body: string; screenshot: string | null }

function MessageBubble({ message, animate }: { message: FeedbackChatMessage; animate: boolean }) {
  const mine = message.from === 'user'
  return (
    <li
      className={cn(
        'flex min-w-0 flex-col gap-1',
        mine ? 'items-end' : 'items-start',
        animate && 'animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out motion-reduce:animate-none',
      )}
    >
      <span className="text-xs text-muted-foreground">
        {mine ? 'Sen' : teamLabel(message.author)} {formatMessageTime(message.createdAt)}
      </span>
      <div
        className={cn(
          'min-w-0 max-w-[85%] space-y-2 rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          mine ? 'rounded-br-md bg-brand-purple text-white' : 'rounded-bl-md bg-muted text-foreground',
        )}
      >
        {message.attachment ? <ChatAttachmentImage id={message.attachment.id} /> : null}
        {message.body ? <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.body}</p> : null}
      </div>
    </li>
  )
}

export function FeedbackChat({ item, queryClient, onBack, onRead, onWindowHiddenChange, closeButton }: FeedbackChatProps) {
  const trackingId = item.trackingId
  const query = useQuery(
    {
      queryKey: ['feedback-chat', trackingId],
      queryFn: () => getFeedbackChat(trackingId),
      // Sekme gizliyken tarayıcı sorgusu durur (refetchIntervalInBackground varsayılan kapalı).
      refetchInterval: CHAT_POLL_INTERVAL_MS,
      refetchIntervalInBackground: false,
      staleTime: 0,
    },
    queryClient,
  )
  const messages = useMemo(() => query.data ?? [], [query.data])
  const [draft, setDraft] = useState('')
  const [shot, setShot] = useState<string | null>(null)
  const [originalShot, setOriginalShot] = useState<string | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [pending, setPending] = useState<PendingMessage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const sendingRef = useRef(false)
  const knownIdsRef = useRef<Set<string> | null>(null)
  const markedRef = useRef<string | null | undefined>(undefined)
  const onReadRef = useRef(onRead)
  const onHiddenRef = useRef(onWindowHiddenChange)
  onReadRef.current = onRead
  onHiddenRef.current = onWindowHiddenChange

  // İlk yüklemede var olan mesajlar animasyonsuz; sonradan gelenler yumuşak belirir.
  if (knownIdsRef.current === null && query.isSuccess) {
    knownIdsRef.current = new Set(messages.map((message) => message.id))
  }

  const teamId = latestTeamMessageId(messages)
  useEffect(() => {
    if (!query.isSuccess) return
    const key = teamId ?? ''
    if (markedRef.current === key) return
    markedRef.current = key
    void markFeedbackChatRead(trackingId)
      .then(() => onReadRef.current(trackingId))
      .catch(() => undefined)
  }, [query.isSuccess, teamId, trackingId])

  useEffect(() => {
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [messages.length, pending])

  // Sohbet kapanır/değişirse pencere gizli kalmasın.
  useEffect(() => () => onHiddenRef.current(false), [])

  const title = useMemo(() => deriveChatTitle(messages), [messages])
  const entries = useMemo(() => buildChatEntries(messages), [messages])

  const capturingRef = useRef(false)
  const captureScreenshot = useCallback(async () => {
    if (capturingRef.current || isCapturing || isSending) return
    capturingRef.current = true
    setIsCapturing(true)
    setError(null)
    onHiddenRef.current(true)
    try {
      await nextPaint()
      const dataUrl = await captureViewportScreenshot()
      if (!dataUrl) throw new Error('empty capture')
      setShot(dataUrl)
      setOriginalShot(dataUrl)
      setShowEditor(false)
    } catch {
      setError(CAPTURE_FAILURE_COPY)
    } finally {
      capturingRef.current = false
      onHiddenRef.current(false)
      setIsCapturing(false)
      window.setTimeout(() => composerRef.current?.focus(), 0)
    }
  }, [isCapturing, isSending])

  const removeScreenshot = useCallback(() => {
    setShot(null)
    setOriginalShot(null)
    setShowEditor(false)
  }, [])

  const send = useCallback(async () => {
    const text = draft.trim()
    if ((!text && !shot) || sendingRef.current) return
    sendingRef.current = true
    setIsSending(true)
    setError(null)
    const outgoing: PendingMessage = { body: text, screenshot: shot }
    setPending(outgoing)
    setDraft('')
    setShot(null)
    setOriginalShot(null)
    setShowEditor(false)
    try {
      await sendFeedbackChatMessage(trackingId, { body: text, screenshot: outgoing.screenshot })
      await query.refetch()
      setPending(null)
    } catch (sendError) {
      setPending(null)
      setDraft(text)
      setShot(outgoing.screenshot)
      setOriginalShot(outgoing.screenshot)
      setError(chatSendErrorMessage(sendError))
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }, [draft, query, shot, trackingId])

  const canSend = (draft.trim().length > 0 || shot !== null) && !isSending && !isCapturing

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="feedback-chat">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3 sm:px-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border border-border px-3 text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2 motion-reduce:transition-none"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Bildirimlerim
        </button>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold">{title}</h3>
          <p className="truncate font-mono text-xs text-muted-foreground">
            {item.trackingId} · {formatFeedbackDate(item.createdAt)}
          </p>
        </div>
        <span className={cn('shrink-0 rounded-full border px-3 py-1 text-xs font-medium', STATUS_STYLES[item.status])}>
          {item.statusLabel}
        </span>
        {closeButton}
      </header>

      <div
        ref={listRef}
        role="log"
        aria-label="Sohbet mesajları"
        aria-live="polite"
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6"
      >
        {query.isPending ? (
          <div role="status" aria-busy="true" className="flex h-full min-h-40 items-center justify-center gap-3 text-sm text-muted-foreground">
            <RefreshCw className="size-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            Sohbet yükleniyor
          </div>
        ) : query.isError && !query.data ? (
          <div role="alert" className="flex min-h-40 flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-muted-foreground">{LOAD_FAILURE_COPY}</p>
            <button type="button" onClick={() => void query.refetch()} className="min-h-11 rounded-full border border-border px-4 text-sm font-medium">
              Tekrar dene
            </button>
          </div>
        ) : messages.length === 0 && !pending ? (
          <p className="flex min-h-40 items-center justify-center text-center text-sm text-muted-foreground">
            Henüz mesaj yok. Aşağıdan yazabilirsin; ekibimiz yanıtladığında burada göreceksin.
          </p>
        ) : (
          <ol className="space-y-4">
            {entries.map((entry) =>
              entry.kind === 'day' ? (
                <li key={entry.key} className="flex justify-center">
                  <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">{entry.label}</span>
                </li>
              ) : (
                <MessageBubble
                  key={entry.key}
                  message={entry.message}
                  animate={knownIdsRef.current !== null && !knownIdsRef.current.has(entry.message.id)}
                />
              ),
            )}
            {pending ? (
              <li className="flex flex-col items-end gap-1 animate-in fade-in duration-200 motion-reduce:animate-none" aria-label="Gönderiliyor">
                <span className="text-xs text-muted-foreground">Sen · gönderiliyor…</span>
                <div className="min-w-0 max-w-[85%] space-y-2 rounded-2xl rounded-br-md bg-brand-purple/70 px-4 py-2.5 text-sm leading-relaxed text-white">
                  {pending.screenshot ? <img src={pending.screenshot} alt="Gönderilen ekran görüntüsü" className="max-h-64 w-auto max-w-full rounded-lg object-contain" /> : null}
                  {pending.body ? <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{pending.body}</p> : null}
                </div>
              </li>
            ) : null}
          </ol>
        )}
      </div>

      <footer className="space-y-3 border-t border-border px-4 py-3 sm:px-6">
        {shot ? (
          <div className="space-y-3 animate-in fade-in slide-in-from-bottom-1 duration-200 motion-reduce:animate-none">
            {showEditor ? (
              <ScreenshotEditor
                source={shot}
                originalSource={originalShot ?? shot}
                included
                isSubmitting={isSending}
                isCropped={shot !== originalShot}
                onIncludedChange={removeScreenshot}
                onSourceChange={setShot}
                onReset={() => setShot(originalShot)}
                onEditingEnd={() => setShowEditor(false)}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <img src={shot} alt="Eklenecek ekran görüntüsü önizlemesi" className="h-12 w-20 rounded-lg border border-border object-cover" />
                <button type="button" onClick={() => setShowEditor(true)} className="min-h-11 text-sm text-brand-purple underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">
                  Düzenle / kırp
                </button>
                <button type="button" onClick={removeScreenshot} className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">
                  <X className="size-4" aria-hidden="true" />
                  Görüntüyü kaldır
                </button>
                <p className="min-w-0 flex-1 text-xs leading-relaxed text-warning-foreground">{SCREENSHOT_NOT_MASKED_COPY}</p>
              </div>
            )}
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-3 sm:flex-nowrap">
          <button
            type="button"
            onClick={() => void captureScreenshot()}
            disabled={isCapturing || isSending}
            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border border-border px-4 text-sm font-medium transition-colors duration-200 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2 disabled:opacity-50 motion-reduce:transition-none"
          >
            {isCapturing ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Camera className="size-4" aria-hidden="true" />}
            Ekran görüntüsü al
          </button>
          <div className="min-w-0 flex-1">
            <label htmlFor="feedback-chat-message" className="sr-only">Mesajın</label>
            <textarea
              id="feedback-chat-message"
              ref={composerRef}
              value={draft}
              rows={1}
              maxLength={CHAT_MAX_CHARS}
              placeholder="Mesajını yaz…"
              disabled={isSending}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault()
                  void send()
                }
              }}
              className="block max-h-32 min-h-11 w-full resize-none rounded-2xl border border-border bg-card px-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple disabled:opacity-60"
            />
            <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground" aria-label="Karakter sayacı">
              {draft.length}/{CHAT_MAX_CHARS}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void send()}
            disabled={!canSend}
            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-brand-purple px-5 text-sm font-medium text-white transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2 disabled:opacity-50 motion-reduce:transition-none"
          >
            {isSending ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Send className="size-4" aria-hidden="true" />}
            Gönder
          </button>
        </div>
      </footer>
    </div>
  )
}
